import type { ReactNode } from 'react';
import { Sparkline, delta } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { CustomerVolume } from '../types';
import type { AccountSummary, CreditFlow } from './account-types';
import { paceWord, pitchWord, planName } from './account-words';
import { PaceTile } from './PaceTile';
import { RunwayTile } from './RunwayTile';
import { weeklySums, windowSum } from './volume-math';
import '../admin.css';
import './insights.css';

export interface AccountHeaderProps {
  account: AccountSummary;
  /** This period's credit flow, on the customer page only: the runway tile's arithmetic. */
  flow?: CreditFlow | undefined;
  /** Absent while it loads; the plan and money show regardless. */
  volume?: CustomerVolume | undefined;
  /** The computed line under the company's name. */
  line: string;
  today: string;
}

function PagesTile({ volume, f }: { volume: CustomerVolume | undefined; f: Formatter }): ReactNode {
  const now = volume ? windowSum(volume.days, 30) : 0;
  const change = volume ? delta(now, windowSum(volume.days, 30, 30)) : null;
  const weeks = volume ? weeklySums(volume.days, 12) : [];
  return (
    <a className="ad-hero__tile" href="#ad-daily">
      <span className="ad-kpi__label">Pages, last 30 days</span>
      <span className="ad-hero__value">{volume ? f.int(now) : '…'}</span>
      <span className="ad-kpi__foot">
        {change ? (
          <span className={`ad-today__delta ad-today__delta--${change.tone}`}>{change.text}</span>
        ) : null}
        vs the 30 before
        {weeks.length > 1 ? (
          <Sparkline
            values={weeks}
            width={96}
            height={24}
            tone="accent"
            label={`Pages a week for 12 weeks, oldest first: ${weeks.join(', ')}`}
          />
        ) : null}
      </span>
    </a>
  );
}

/** What a page brings in: a price, never a cost (the revenue screen says the same). */
function paidAPage(a: AccountSummary, volume: CustomerVolume | undefined): string | null {
  const pages = volume ? windowSum(volume.days, 30) : 0;
  if (pages <= 0 || a.money.paidLast30Days <= 0) return null;
  return `$${(a.money.paidLast30Days / pages).toFixed(4)} paid a page`;
}

/** The pace against the plan in words that never call 4× "within plan". */
function paceFact(a: AccountSummary): string {
  const c = a.credits;
  if (c.pace === null) return 'no plan pages';
  const held =
    c.heldDays > 0
      ? `, held ${c.heldDays} day${c.heldDays === 1 ? '' : 's'} before the refill`
      : '';
  return c.pace > 1
    ? `over plan: ${paceWord(c.pace)}${held}`
    : `within plan at this pace (${paceWord(c.pace)})`;
}

function moneyItems(
  a: AccountSummary,
  volume: CustomerVolume | undefined,
  f: Formatter,
): [string, string][] {
  const price =
    a.plan.listPriceDollars !== null
      ? `${f.moneyWhole(a.plan.listPriceDollars)} a month list`
      : 'quoted';
  const perPage = paidAPage(a, volume);
  const paid = `${f.money(a.money.paidLast30Days)} in 30 days · ${f.money(a.money.paidInAll)} in all`;
  const pitch = pitchWord(a, (n) => f.int(n));
  return [
    [
      'Plan',
      `${planName(a.plan.key)} · ${price} · ${a.plan.card ? 'pays by card' : 'no card'}${a.plan.pastDue ? ' · past due' : ''}`,
    ],
    ['Paid', perPage ? `${paid} · ${perPage}` : paid],
    ['Pace', paceFact(a)],
    ...(pitch ? [['Pitch', pitch] as [string, string]] : []),
    ['Partner', a.plan.partner ?? 'none'],
    ['Phone Home', a.plan.phoneHomeCustomerId ?? 'not linked'],
  ];
}

/** The plan, the money, the pitch and the partner in one quiet row: what the account is worth. */
function MoneyRow(props: {
  a: AccountSummary;
  volume: CustomerVolume | undefined;
  f: Formatter;
}): ReactNode {
  return (
    <dl className="ad-money" id="ad-money">
      {moneyItems(props.a, props.volume, props.f).map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The top of the customer page (round 2): the computed line, three hero tiles (pages and their
 * direction; the pace on the plan, with a labelled bullet; the day the credits run out, with the
 * arithmetic), then the money row with the pitch. Every number comes from the one account reader
 * and every tile leads to its evidence.
 */
export function AccountHeader({
  account: a,
  flow,
  volume,
  line,
  today,
}: AccountHeaderProps): ReactNode {
  const f = useFormat();
  return (
    <section className="ad-account" aria-label="The account">
      <p className="ad-account__line">{line}</p>
      <div className="ad-hero">
        <PagesTile volume={volume} f={f} />
        <PaceTile a={a} today={today} f={f} />
        <RunwayTile a={a} flow={flow} f={f} />
      </div>
      <MoneyRow a={a} volume={volume} f={f} />
    </section>
  );
}
